import { Component, computed, effect, ElementRef, HostListener, input, output, Renderer2, signal, ViewChild } from '@angular/core';
import { JSBridgeService } from '../../core/jsbridge.service';
import { KopConfigService } from '../../core/kop-config.service';
import { Losdef, MENUItem } from '@wasm/KopWeb';
import { IOBaseType, IODirection, LosDefType, ModuleLosdef, RangedKanaal, RangedKanaalInput } from '../PropTypes';
import { LDMenuService, MenuItem } from '../../core/ldmenu.service';
import { KanaalService } from '../../kanaal/kanaal.service';
import { wasmVectorFindIndex, wasmVectorToArray } from '../../utils/wasmVector';
import { TranslateService } from '../../i18n/translate.service';
import { BaseElementComponent } from '../../keynav/base-element/base-element.component';
import { FocusManagerService } from '../../keynav/focus-manager.service';


const IOBaseTypeShort: Partial<Record<IOBaseType, string>> = {
  [IOBaseType.Analog]: 'A',
  [IOBaseType.Digital]: 'D',
  [IOBaseType.ModBus]: 'ModBus',
}

const IODirectionShort: Partial<Record<IODirection, string>> = {
  [IODirection.IN]: 'I',
  [IODirection.OUT]: 'O',
}
@Component({
  selector: 'los-def-editor',
  templateUrl: './los-def-editor.component.html',
  styleUrl: './los-def-editor.component.scss',
  animations: []
})
export class LosDefEditorComponent extends BaseElementComponent{
  constructor(private kopConfigService: KopConfigService, public jsBridgeService: JSBridgeService,
    public menuService: LDMenuService, public kanaalService: KanaalService, public renderer: Renderer2, private translateService: TranslateService, 
    focusManager: FocusManagerService, elementRef: ElementRef<HTMLElement>) {
    super(elementRef,focusManager);
    this.keyProperty = "los-def-editor";
    effect(() => {
      let ld = this.initialValue();
      if (ld != undefined) {
        this.editVal = { ...ld }
      }
    });
  }

  public meta = computed(() => {
    let ld = this.initialValue();
    if (ld != undefined) {
      let meta = this.kopConfigService.currentMeta();
      return meta.losDefRaw.find(m => m.index == ld.index);
    }
    let hmeta = this.selectedMeta();
    if (hmeta != undefined) {
      return hmeta;
    }
  });

  public assignment = computed(() => {
    let meta = this.meta();
    let afd = this.kopConfigService.selectedAfd();
    if (meta != undefined && afd != undefined) {
      return this.kanaalService.buildAssignementInfo(this.ldType(), afd?.afdeling(), meta);
    }

  });


  public ldType = input<LosDefType>('losdef');

  public valueChanged = output<ModuleLosdef>();

  public editOnly = input<boolean>(false);
  public autoFill = input<boolean>(false);
  public isNew = input<boolean>(false);

  public editVal: ModuleLosdef = {
    index: 0,
    type: 0,
    // waarde: 0,
    inverted: false
  };

  isChecked(item: MENUItem) {
    let waarde = this.editVal.waarde == undefined ? 0 : this.editVal.waarde;
    if (waarde != undefined) {
      return (waarde & item.mask) == item.value;
    }
    return false;
  }
  public submenues = computed(() => {
    let meta = this.meta();
    if (meta?.menunr != undefined && meta.menunr > 0) {
      let menus = this.menuService.getMenu(meta.menunr);
      let ret: MenuItem[][] = [];
      let submenu = -1;
      let current: MenuItem[] = [];
      for (let item of menus) {
        if ((submenu != -1) && (item.submenu != submenu)) {
          ret.push(current);
        }
        if (item.submenu != submenu) {
          submenu = item.submenu;
          current = [];
        }
        current.push(item);
      }
      if (current.length > 0) {
        ret.push(current);
      }
      return ret;
    }
    return [];
  });

  public computedWidth = computed(() => {
    let submenues = this.submenues();
    if (submenues.length > 0) {
      return 300 * submenues.length;
    }
    return 300;
  });

  public valueLabel = computed(() => {
    let meta = this.meta();
    let ld = this.initialValue();
    if ((meta != undefined) && (ld != undefined) && (ld.waarde != undefined)) {
      if (meta.menunr > 0) {
        return this.menuService.valueLabel(meta.menunr, ld.waarde);
      } else if (ld.waarde != 0) {
        return `${ld.waarde}${ld.inverted ? '!' : ''} ${this.typeLabel()}`
      }
    }
    return "";
  });

  onCheckboxChange(e: boolean | string, item: MENUItem) {
    let w = this.editVal.waarde != undefined ? this.editVal.waarde : 0;
    let v = (w & ~item.mask) | ((e == true) ? item.value : 0);
    this.editVal.waarde = v;
    this.valueChanged.emit(this.editVal);
  }

  onKanaalInputChanged(event: RangedKanaalInput) {
    this.editVal.waarde = event.start;
    this.editVal.inverted = event.inverted == true;
    if (this.editOnly() && this.editVal.index != 0) {
      this.valueChanged.emit(this.editVal);
    }
  }

  public initialValue = input<ModuleLosdef>();

  // private _initialValue = computed(() => {
  //   let inputValue = this.initialValue();
  //   if (inputValue != undefined) {
  //     return {...inputValue};
  //   }
  //   return this.kopConfigService.findOrBuildLosDef(this.meta()!, this.ldType());
  // });

  public ioType = computed(() => {
    let meta = this.meta();
    if (meta != undefined) {
      return this.kanaalService.getIOTypeFromParamType(meta.type)
    }
    return undefined;
  });

  public paramRange = computed(() => {
    let meta = this.meta();
    if (meta != undefined) {
      return this.kanaalService.getRangeFromParamType(meta.type);
    }
    return {
      min: 0,
      max: 255  
    };
  })

  public typeLabel = computed(() => {
    let ioType = this.ioType();
    if (ioType != undefined) {
      return `${IOBaseTypeShort[ioType.type]}${IODirectionShort[ioType.direction]}`;
    }
    return "";
  });


  onSave() {
    if (this.editVal.waarde == undefined) {
      this.editVal.waarde = 0;
    }
    this.kopConfigService.upsertLosDef(this.editVal, this.ldType());
    this.close();
  }


  @ViewChild("anchor", { read: ElementRef }) public anchor?: ElementRef = undefined;
  @ViewChild("popup", { read: ElementRef }) public popup?: ElementRef = undefined;
  private unlistener?: () => void;

  public toggleEdit() {
    this.isEditing = !this.isEditing;
    if (this.isEditing) {
      this.unlistener = this.renderer.listen("document", "click", (event) => {
        if (!this.contains(event.target)) {
          this.toggleEdit();
        }
      })
    } else if (this.unlistener != undefined) {
      this.unlistener();
    }

  }

  private contains(target: EventTarget): boolean {
    return (
      (this.anchor && this.anchor.nativeElement.contains(target)) ||
      (this.popup ? this.popup.nativeElement.contains(target) : false)
    );
  }


  public availableMetas = computed(() => {
    switch (this.ldType()) {
      case 'losdef':
        return this.kopConfigService.currentMeta().losDefs;
      case 'droogwand':
        return this.kopConfigService.currentMeta().drogWands;
      case 'koeling':
        return this.kopConfigService.currentMeta().koelings;
      case 'verwarming':
        return this.kopConfigService.currentMeta().verwarmings;
    }
  });

  public selectedMeta = signal<Losdef | undefined>(undefined);


  onMetaSelected(ldmb: any) {
    let ldm = ldmb as Losdef;
    let ld = this.kopConfigService.findOrBuildLosDef(ldm, this.ldType());
    this.selectedMeta.set(ldm);
    this.editVal = ld;
    this.isEditing = true;
  }

  alreadySelectedFN() {
    let afd = this.kopConfigService.selectedAfd()?.afdeling();
    let ret = (idx: number) => {
      if (afd != undefined) {
        let v = [];
        let fidx = -1;
        switch (this.ldType()) {
          case 'losdef':
            fidx = wasmVectorFindIndex(afd.losDefs, (ld) => ld.index == idx && ld.waarde != 0);
            break;
          case 'droogwand':
            fidx = wasmVectorFindIndex(afd.droogWand, (ld) => ld.index == idx && ld.waarde != 0);
            break;
          case 'koeling':
            fidx = wasmVectorFindIndex(afd.koeling, (ld) => ld.index == idx && ld.waarde != 0);
            break;
          case 'verwarming':
            fidx = wasmVectorFindIndex(afd.verwarming, (ld) => ld.index == idx && ld.waarde != 0);
            break;
        }
        let ret = fidx >= 0;
        return ret;
      }
      return false;
    }
    return ret;
  }

  labelFn() {
    let ret = (meta: any) => {
      //(meta as Losdef).omschr.toString()
      let m = meta as Losdef;
      return this.translateService.translateKopData('losdef', m.index,  m.omschr?.toString() );
    };
    return ret;
  }

  onDelete() {
    this.kopConfigService.applyAfdUpdate(afd => {
      if ((this.editVal != undefined) && (afd != undefined)) {
        this.kopConfigService.eraseLosDef(afd, this.editVal, this.ldType());
      }
      return afd;
    });
    this.close();
  }
}
